import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Modal, Spinner } from "react-bootstrap";
import { fetchSessionHistory } from "../../api";

/**
 * A note's audit trail: every change to it, newest first, with who made it,
 * when, from which client, and what changed. Backed by GET
 * /sessions/:id/history, which reads the sessionAudit collection on demand;
 * nothing here loads with the patient file.
 *
 * Values are rendered as TEXT only. Notepad content is HTML in the database,
 * so it is flattened to its words before display; nothing from the audit
 * rows is ever set as markup.
 */

const ACTION_LABELS = {
  created: "Created",
  updated: "Edited",
  deleted: "Deleted",
  linked: "Linked to patient",
  synctoApplied: "SyncTo applied",
  consent: "Recording consent",
};

const ACTION_VARIANT = {
  created: "success",
  updated: "secondary",
  deleted: "danger",
  linked: "info",
  synctoApplied: "primary",
  consent: "secondary",
};

const FIELD_LABELS = {
  sessionsNoteTitle: "Title",
  notepadContent: "Note text",
  notepadContentType: "Note format",
  sessionType: "Note kind",
  sessionsTemplateId: "Template",
  sessionsTemplateVersion: "Template version",
  sessionsServiceName: "Service",
  sessionsAppointmentId: "Appointment",
  sessionsAppointmentStart: "Appointment date",
  sessionsPatientId: "Patient",
  sessionsPracticeId: "Practice",
  sessionsResourceId: "Practitioner",
  sessionsAdHoc: "Ad hoc",
  sessionsAdHocGroupId: "Ad hoc sitting",
  sessionsInitial: "Initial assessment",
  syncToMode: "SyncTo mode",
  syncToConsentAt: "Recording consent",
  handwritingImage: "Handwriting",
  sections: "Template sections",
  mediaFiles: "Photos",
  templatePages: "Template pages",
  syncToTranscript: "SyncTo transcript",
  syncToCleaned: "SyncTo cleaned text",
};

/** Fields whose values are dates, shown in en-GB rather than as ISO text. */
const DATE_FIELDS = new Set(["sessionsAppointmentStart", "syncToConsentAt"]);

/** "someCamelCase" as "Some camel case", for anything not in the maps. */
const sentenceCase = (s) =>
  String(s || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());

// A template field is named by its label when the server could resolve
// one, with the id kept beside it in small type; otherwise by its id alone.
const fieldLabel = (field, labels = {}) => {
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  if (field.startsWith("capturedValues.")) {
    const id = field.slice("capturedValues.".length);
    const label = labels[id];
    return label ? (
      <>
        {label} <span className="small text-muted fw-normal">({id})</span>
      </>
    ) : `Field: ${id}`;
  }
  return sentenceCase(field);
};

const formatWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

/** A stored value as readable text, with any HTML flattened to its words. */
const asText = (v, field) => {
  if (v === null || v === undefined || v === "") return "";
  if (field && DATE_FIELDS.has(field) && typeof v === "string") {
    const when = formatWhen(v);
    if (when) return when;
  }
  if (typeof v === "object") {
    if (v.truncated && typeof v.text === "string") return `${stripHtml(v.text)} …(truncated)`;
    if (v.preview !== undefined) return `${v.preview}${v.fingerprint ? ` (${formatBytes(v.fingerprint.bytes)})` : ""}`;
    if (v.sha1) return `${formatBytes(v.bytes)} of content`;
    try { return JSON.stringify(v, null, 1); } catch (_e) { return String(v); }
  }
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return stripHtml(String(v));
};

function stripHtml(s) {
  if (typeof window === "undefined" || !window.DOMParser) return s.replace(/<[^>]*>/g, " ");
  const doc = new window.DOMParser().parseFromString(`<div>${s}</div>`, "text/html");
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
}

function formatBytes(n) {
  if (!Number.isFinite(n)) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const LONG = 400;

function ValueBlock({ label, value, tone, field }) {
  const [open, setOpen] = useState(false);
  const text = asText(value, field);
  if (!text) return <div className="small text-muted fst-italic">{label}: empty</div>;
  const long = text.length > LONG;
  return (
    <div className="small">
      <span className={`fw-semibold ${tone}`}>{label}: </span>
      <span style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
        {long && !open ? `${text.slice(0, LONG)}…` : text}
      </span>
      {long && (
        <Button variant="link" size="sm" className="p-0 ms-1 align-baseline text-decoration-none" onClick={() => setOpen((o) => !o)}>
          {open ? "Show less" : "Show all"}
        </Button>
      )}
    </div>
  );
}

function ChangeRow({ change, labels }) {
  const label = fieldLabel(change.field, labels);
  if (change.kind === "binary") {
    const parts = [];
    if (change.beforeCount != null || change.afterCount != null) {
      parts.push(`${change.beforeCount ?? 0} to ${change.afterCount ?? 0} item${(change.afterCount ?? 0) === 1 ? "" : "s"}`);
    } else if (!change.before && change.after) {
      parts.push(`added (${formatBytes(change.after.bytes)})`);
    } else if (change.before && !change.after) {
      parts.push(`removed (was ${formatBytes(change.before.bytes)})`);
    } else {
      parts.push(`replaced (${formatBytes(change.before?.bytes)} to ${formatBytes(change.after?.bytes)})`);
    }
    return (
      <li className="mb-1">
        <span className="fw-semibold text-primary">{label}</span>{" "}
        <span className="small">{parts.join(", ")}</span>
        {Array.isArray(change.added) && change.added.length > 0 && (
          <div className="small text-success">Added: {change.added.join(", ")}</div>
        )}
        {Array.isArray(change.removed) && change.removed.length > 0 && (
          <div className="small text-danger">Removed: {change.removed.join(", ")}</div>
        )}
      </li>
    );
  }
  return (
    <li className="mb-2">
      <div className="fw-semibold text-primary">{label}</div>
      <ValueBlock label="Before" value={change.before} tone="text-danger" field={change.field} />
      <ValueBlock label="After" value={change.after} tone="text-success" field={change.field} />
    </li>
  );
}

export default function NoteAuditModal({ show, onHide, sessionId, noteTitle }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [entries, setEntries] = useState([]);
  const [deleted, setDeleted] = useState(false);
  // Template field id to label, resolved by the server from the note.
  const [fieldLabels, setFieldLabels] = useState({});

  useEffect(() => {
    if (!show || !sessionId) {
      // Nothing from the last note lingers into the next open.
      setEntries([]);
      setDeleted(false);
      setFieldLabels({});
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setEntries([]);
    setFieldLabels({});
    setLoading(true);
    setError(null);
    fetchSessionHistory(sessionId)
      .then((res) => {
        if (cancelled) return;
        setEntries(res.entries);
        setDeleted(res.deleted);
        setFieldLabels(res.fieldLabels || {});
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Could not load the note's audit trail:", err);
        setError("The audit trail could not be loaded. Please try again.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [show, sessionId]);

  const title = useMemo(
    () => (noteTitle ? `Audit trail: ${noteTitle}` : "Audit trail"),
    [noteTitle],
  );

  return (
    <Modal show={show} onHide={onHide} centered size="lg" scrollable>
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading audit trail…</p>
          </div>
        ) : error ? (
          <p className="text-danger mb-0">{error}</p>
        ) : entries.length === 0 ? (
          <div className="text-center py-4">
            <i className="fa-light fa-clock-rotate-left d-block mb-2 text-primary" style={{ fontSize: "1.75rem" }} />
            <p className="mb-1">No audit trail recorded yet for this note.</p>
            <p className="small text-muted mb-0">
              Changes are recorded from the moment the audit trail was switched on. Edits made before that are not listed.
            </p>
          </div>
        ) : (
          <>
            {deleted && (
              <p className="small text-danger">This note has been deleted. Its audit trail is kept.</p>
            )}
            <div className="d-flex flex-column gap-3">
              {entries.map((e) => (
                <div key={e._id} className="border-start border-3 border-primary ps-3">
                  <div className="d-flex align-items-center flex-wrap gap-2">
                    <Badge bg={ACTION_VARIANT[e.action] || "secondary"}>{ACTION_LABELS[e.action] || sentenceCase(e.action)}</Badge>
                    <span className="fw-semibold">{e.actor?.name || "Unknown user"}</span>
                    <span className="small text-muted">
                      {e.firstAt && e.editCount
                        ? `${formatWhen(e.firstAt)} to ${formatWhen(e.at)} (${e.editCount + 1} saves)`
                        : formatWhen(e.at)}
                    </span>
                    {e.source && <Badge bg="light" text="dark">{sentenceCase(e.source)}</Badge>}
                  </div>
                  {e.note && <div className="small text-muted mt-1">{e.note}</div>}
                  {Array.isArray(e.changes) && e.changes.length > 0 ? (
                    <ul className="mt-2 mb-0 ps-3">
                      {e.changes.map((c, i) => <ChangeRow key={`${e._id}-${c.field}-${i}`} change={c} labels={fieldLabels} />)}
                    </ul>
                  ) : e.action === "deleted" ? (
                    <div className="small text-muted mt-1">The full note as it stood was kept with this entry.</div>
                  ) : null}
                </div>
              ))}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
